// "DOK Checklist" tab — generates a blank, printable DOK Skills
// Checklist (.docx) for a chosen Subject/Unit, so a teacher can
// print it and hand-mark a student's skills/proficiency during an
// in-person observation. This reads only curriculum data (the
// unit's skills grouped by DOK level) — it does not read or write
// any saved assessment data, and the generated document carries no
// student-specific values.

import React, { useState } from "react";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";

import api from "../axios/axiosInstance";
import useSubjectsAndUnits from "../hooks/useSubjectsAndUnits";
import { PILL_FIELD_SX } from "./batchAssessment/batchAssessmentColors";
import { buildDokChecklistData } from "./dokChecklist/dokChecklistData";
import { downloadDokChecklistWord } from "./dokChecklist/dokChecklistDocx";

const DokChecklistGenerator = () => {
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedUnit, setSelectedUnit] = useState("");

    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);

    const { subjects, units } = useSubjectsAndUnits(selectedSubject);

    const handleSubjectChange = (value) => {
        setSelectedSubject(value);
        setSelectedUnit("");
        setError(null);
    };

    const handleUnitChange = (value) => {
        setSelectedUnit(value);
        setError(null);
    };

    const handleGenerate = async () => {
        if (!selectedSubject || !selectedUnit) {
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            const res = await api.get(
                `${process.env.REACT_APP_BACKEND_URL}/grades/edit-subject-unit-skills/`,
                {
                    params: {
                        subject_id: selectedSubject,
                        unit_id: selectedUnit,
                    },
                    withCredentials: true,
                }
            );

            const checklistData = buildDokChecklistData({
                subjectName: res.data?.subject_name,
                unitName: res.data?.unit_name,
                skills: res.data?.skills || [],
            });

            if (!checklistData.dokGroups.length) {
                setError(
                    "This unit doesn't have any skills yet. Add skills under Manage Subjects & Units first."
                );
                return;
            }

            await downloadDokChecklistWord(checklistData);
        } catch (err) {
            console.error("Failed to generate DOK checklist:", err);
            setError("Unable to generate the checklist. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Box>
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        DOK Checklist
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Generate a printable, blank DOK Skills Checklist for a
                        Subject and Unit — hand it out or keep it on a
                        clipboard to mark a student's skills and proficiency
                        while observing them, then transfer the results into
                        Record Assessment afterward.
                    </Typography>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                        Choose Subject &amp; Unit
                    </Typography>

                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Subject"
                                value={selectedSubject}
                                onChange={(event) =>
                                    handleSubjectChange(event.target.value)
                                }
                                sx={PILL_FIELD_SX}
                            >
                                {subjects.map((subject) => (
                                    <MenuItem key={subject.id} value={subject.id}>
                                        {subject.subject_name}
                                        {subject.class_level
                                            ? ` - Class ${subject.class_level}`
                                            : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Unit"
                                value={selectedUnit}
                                disabled={!selectedSubject}
                                onChange={(event) =>
                                    handleUnitChange(event.target.value)
                                }
                                sx={PILL_FIELD_SX}
                            >
                                {units.map((unit) => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                        {unit.unit_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={12} md={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={
                                    !selectedSubject ||
                                    !selectedUnit ||
                                    isGenerating
                                }
                                onClick={handleGenerate}
                                startIcon={
                                    isGenerating ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        <DownloadIcon />
                                    )
                                }
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 999,
                                    py: 1,
                                }}
                            >
                                {isGenerating
                                    ? "Generating..."
                                    : "Generate Checklist (.docx)"}
                            </Button>
                        </Grid>
                    </Grid>

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                <Alert icon={<FactCheckOutlined fontSize="small" />} severity="info">
                    The checklist lists every skill for the chosen unit,
                    grouped by DOK level, with blank checkboxes and an
                    Emerging / Developing / Proficient / Extending selector
                    for each level — Student name, Class and Date are left
                    blank for you to fill in by hand.
                </Alert>
            </Stack>
        </Box>
    );
};

export default DokChecklistGenerator;
